import React, { useRef, useState, useEffect } from "react";
import { Box, Avatar } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { useFileUploadMutation, useFileDeleteMutation } from "@mutation/fileMutation";
import { useFileQuery } from "@query/fileQuery";
import ImagePlaceholder from "@component/ImagePlaceholder";

const ImageUploadPreview = ({
  mode = "show",
  fileUuid = null,
  onUpload,
  onDelete,
  sx = {},
  imageSx = {}
}) => {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hover, setHover] = useState(false);
  
  const uploadMutation = useFileUploadMutation();
  const deleteMutation = useFileDeleteMutation();
  const fileQuery = useFileQuery(fileUuid);

  useEffect(() => {
    if (fileQuery.isSuccess && fileQuery.data?.data instanceof Blob) {
      const url = URL.createObjectURL(fileQuery.data?.data);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [fileQuery.data, fileQuery.isSuccess]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Delete old image if exists
    if (fileUuid) {
      deleteMutation.mutate({ data: { file_uuid: fileUuid } });
      onDelete?.();
    }
    
    // Upload new image
    const formData = new FormData();
    formData.append("file", file);
    
    uploadMutation.mutate(
      { data: formData },
      {
        onSuccess: (data) => {
          onUpload?.(data?.data?.uuid);
          fileInputRef.current.value = '';
        },
      }
    );

    // Create preview
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  if (mode === "show" && !fileUuid) return null;

  return (
    <Box
      sx={{
        width: '100%',
        height: '300px',
        position: 'relative',
        overflow: 'hidden',
        display:"flex",
        justifyContent:"center",
        backgroundColor:"#fff",
        ...sx
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {previewUrl && 
        <Box
          component={previewUrl ? "img" : null}
          src={previewUrl}
          alt="Preview"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: mode === 'edit' ? 'pointer' : 'default',
            display: 'block',
            ...imageSx
          }}
        />
      }      
      {!previewUrl && 
        <ImagePlaceholder/>
      }
      {mode === "edit" && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
          {hover && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)',
                cursor: 'pointer',
              }}
              onClick={triggerFileInput}
            >
              <UploadIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ImageUploadPreview;